import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "../../lib/api";
import { DATA_QUALITY, fmtDate, fmtMoney, PHASE_LABELS, PROJECT_HEALTH, PROJECT_STATUS } from "../../lib/labels";
import { userName, useUsers } from "../../lib/hooks";
import { Chip, EmptyState, PageHeader } from "../../components/common";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

export default function Portafolio() {
  const navigate = useNavigate();
  const { map } = useUsers();
  const [phase, setPhase] = useState("");
  const [health, setHealth] = useState("");
  const { data: items } = useQuery({
    queryKey: ["proj-portfolio"],
    queryFn: async () => (await api.get("/proyectos/portfolio")).data,
  });
  const filtered = (items || []).filter((p) => (!phase || p.lifecycle_phase === phase) && (!health || p.health_status === health));

  return (
    <div data-testid="portafolio-page">
      <PageHeader title="Portafolio" subtitle="Vista comparativa de todos los proyectos visibles: fase, salud, presupuesto, forecast y avance." />
      <div className="mb-4 flex flex-wrap gap-2">
        <Select value={phase || "all"} onValueChange={(v) => setPhase(v === "all" ? "" : v)}>
          <SelectTrigger className="w-52" data-testid="portfolio-phase-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las fases</SelectItem>
            {Object.entries(PHASE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={health || "all"} onValueChange={(v) => setHealth(v === "all" ? "" : v)}>
          <SelectTrigger className="w-44" data-testid="portfolio-health-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toda salud</SelectItem>
            {Object.entries(PROJECT_HEALTH).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {items && filtered.length === 0 && <EmptyState title="Sin proyectos" description="No hay proyectos con esos filtros." />}
      {filtered.length > 0 && (
        <div className="ts-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Proyecto</TableHead><TableHead>Fase</TableHead><TableHead>Estado</TableHead><TableHead>Salud</TableHead>
                <TableHead>PM</TableHead><TableHead>Avance</TableHead><TableHead className="text-right">Presupuesto</TableHead>
                <TableHead className="text-right">Forecast</TableHead><TableHead>Fin previsto</TableHead><TableHead>Datos</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((p) => (
                <TableRow key={p.id} data-testid={`portfolio-row-${p.id}`} className="cursor-pointer" onClick={() => navigate(`/proyectos/${p.id}`, { state: { crumb: `${p.project_code} — ${p.name}` } })}>
                  <TableCell>
                    <p className="font-medium text-slate-900">{p.name}</p>
                    <p className="font-mono text-xs text-slate-400">{p.project_code} · {p.municipality}</p>
                  </TableCell>
                  <TableCell className="text-xs font-medium">{PHASE_LABELS[p.lifecycle_phase]}</TableCell>
                  <TableCell><Chip map={PROJECT_STATUS} value={p.status} /></TableCell>
                  <TableCell><Chip map={PROJECT_HEALTH} value={p.health_status} testid={`health-chip-${p.id}`} /></TableCell>
                  <TableCell className="text-xs">{userName(map, p.pm_id)}</TableCell>
                  <TableCell>
                    <div className="h-1.5 w-20 overflow-hidden rounded-full bg-slate-100">
                      <div className="h-full bg-blue-600" style={{ width: `${p.physical_progress || 0}%` }} />
                    </div>
                    <p className="mt-0.5 font-mono text-[10px] text-slate-400">{p.physical_progress}%</p>
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">{fmtMoney(p.current_budget)}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{fmtMoney(p.forecast_final_cost)}</TableCell>
                  <TableCell className="text-xs">{fmtDate(p.current_forecast_completion)}</TableCell>
                  <TableCell><Chip map={DATA_QUALITY} value={p.data_quality_status || "completo"} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
